import React, { useState, useEffect } from "react";
import { 
  Shield, 
  ShieldCheck, 
  ShieldAlert, 
  X, 
  Copy, 
  Check, 
  Download, 
  QrCode, 
  Key, 
  Loader2, 
  AlertTriangle,
  Smartphone,
  CheckCircle2,
  RefreshCw
} from "lucide-react";
import { 
  generateBase32Secret, 
  generateOtpAuthUri, 
  generateQRCodeDataUrl, 
  verifyTOTP, 
  generateBackupCodes, 
  hashBackupCode 
} from "../../services/totpService";
import { logSecurityEvent } from "../../services/AuditLogger";
import { updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase/config";

export default function MFAEnrollmentModal({
  isOpen,
  onClose,
  userDocId,
  userEmail,
  isMfaEnabled,
  onMfaStatusChange
}) {
  const [step, setStep] = useState("init"); // 'init', 'setup', 'verify', 'backup_codes', 'manage', 'confirm_disable'
  const [secret, setSecret] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);
  const [disableConfirmCode, setDisableConfirmCode] = useState("");

  useEffect(() => {
    if (!isOpen) {
      // Reset state on close
      setStep("init");
      setSecret("");
      setQrCodeUrl("");
      setVerificationCode("");
      setBackupCodes([]);
      setError("");
      setCopiedKey(false);
      setCopiedBackupCodes(false);
      setDisableConfirmCode("");
      return;
    }

    if (isMfaEnabled) {
      setStep("manage");
    } else {
      initializeEnrollment();
    }
  }, [isOpen, isMfaEnabled]);

  async function initializeEnrollment() {
    try {
      setLoading(true);
      setError("");
      const newSecret = generateBase32Secret(20);
      setSecret(newSecret);

      const uri = generateOtpAuthUri(userEmail || "user@jimini.com", newSecret, "Jimini Logistics");
      const qrUrl = await generateQRCodeDataUrl(uri);
      setQrCodeUrl(qrUrl);
      setStep("setup");
    } catch (err) {
      console.error("Failed to initialize MFA setup:", err);
      setError("Failed to generate authenticator configuration.");
    } finally {
      setLoading(false);
    }
  }

  const handleCopyKey = () => {
    if (!secret) return;
    navigator.clipboard.writeText(secret);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleCopyBackupCodes = () => {
    if (backupCodes.length === 0) return;
    const text = `Jimini Logistics Emergency Recovery Codes:\n\n${backupCodes.join("\n")}\n\nEach code can only be used once. Keep these codes safe.`;
    navigator.clipboard.writeText(text);
    setCopiedBackupCodes(true);
    setTimeout(() => setCopiedBackupCodes(false), 2000);
  };

  const handleDownloadBackupCodes = () => {
    if (backupCodes.length === 0) return;
    const text = `JIMINI LOGISTICS - EMERGENCY RECOVERY CODES
Account: ${userEmail}
Generated: ${new Date().toLocaleString()}

Emergency Backup Codes (Single-Use):
${backupCodes.map((code, idx) => `${idx + 1}. ${code}`).join("\n")}

IMPORTANT:
- Store these codes in a secure password manager or offline location.
- If you lose access to your Microsoft/Google Authenticator app, you can sign in with any unused code.
- Each code can only be used once.`;

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jimini-mfa-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  async function handleVerifyAndEnroll(e) {
    e.preventDefault();
    if (!verificationCode || verificationCode.trim().length !== 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const isValid = await verifyTOTP(secret, verificationCode.trim());
      if (!isValid) {
        setError("Invalid 6-digit code. Ensure your device clock is synchronized and try again.");
        setLoading(false);
        return;
      }

      // Generate 8 emergency recovery codes and hash them
      const rawBackupCodes = generateBackupCodes(8);
      const hashedBackupCodes = await Promise.all(rawBackupCodes.map(code => hashBackupCode(code)));

      // Update Firestore user document
      if (userDocId) {
        const userRef = doc(db, "users", userDocId);
        await updateDoc(userRef, {
          mfaEnabled: true,
          mfaSecret: secret,
          mfaBackupCodes: hashedBackupCodes,
          mfaEnrolledAt: new Date().toISOString()
        });
      }

      // Log Security Audit Event
      logSecurityEvent({
        action: "AUTH_MFA_ENROLLED",
        category: "AUTH",
        userEmail: userEmail,
        status: "SUCCESS",
        severity: "INFO",
        details: { method: "TOTP_RFC6238", backupCodesGenerated: 8 }
      });

      setBackupCodes(rawBackupCodes);
      setStep("backup_codes");
      if (onMfaStatusChange) onMfaStatusChange(true);
    } catch (err) {
      console.error("Failed to enable MFA:", err);
      setError("Failed to save MFA configuration. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDisableMFA() {
    try {
      setLoading(true);
      setError("");

      if (userDocId) {
        const userRef = doc(db, "users", userDocId);
        await updateDoc(userRef, {
          mfaEnabled: false,
          mfaSecret: null,
          mfaBackupCodes: [],
          mfaDisabledAt: new Date().toISOString()
        });
      }

      // Log Security Audit Event
      logSecurityEvent({
        action: "AUTH_MFA_DISABLED",
        category: "AUTH",
        userEmail: userEmail,
        status: "SUCCESS",
        severity: "WARNING",
        details: { method: "TOTP_RFC6238" }
      });

      if (onMfaStatusChange) onMfaStatusChange(false);
      onClose();
    } catch (err) {
      console.error("Failed to disable MFA:", err);
      setError("Failed to disable Two-Factor Authentication.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegenerateBackupCodes() {
    try {
      setLoading(true);
      setError("");

      const rawBackupCodes = generateBackupCodes(8);
      const hashedBackupCodes = await Promise.all(rawBackupCodes.map(code => hashBackupCode(code)));

      if (userDocId) {
        const userRef = doc(db, "users", userDocId);
        await updateDoc(userRef, {
          mfaBackupCodes: hashedBackupCodes,
          mfaBackupCodesRegeneratedAt: new Date().toISOString()
        });
      }

      logSecurityEvent({
        action: "AUTH_MFA_BACKUP_CODES_REGENERATED",
        category: "AUTH",
        userEmail: userEmail,
        status: "SUCCESS",
        severity: "INFO",
        details: { backupCodesCount: 8 }
      });

      setBackupCodes(rawBackupCodes);
      setStep("backup_codes");
    } catch (err) {
      console.error("Failed to regenerate backup codes:", err);
      setError("Failed to regenerate emergency backup codes.");
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="modal-overlay animate-fade-in" style={{ zIndex: 1100 }}>
      <div 
        className="modal-content glass-card" 
        style={{ 
          maxWidth: step === "setup" ? "560px" : "500px", 
          width: "95%", 
          padding: "2rem",
          position: "relative"
        }}
      >
        {/* Close Button */}
        <button 
          className="btn-icon" 
          onClick={onClose} 
          style={{ position: "absolute", top: "1.25rem", right: "1.25rem" }}
        >
          <X size={20} />
        </button>

        {/* Modal Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
          <div 
            style={{ 
              width: "44px", 
              height: "44px", 
              borderRadius: "10px", 
              backgroundColor: isMfaEnabled ? "rgba(76, 175, 80, 0.15)" : "rgba(33, 150, 243, 0.15)",
              color: isMfaEnabled ? "var(--success)" : "var(--primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {isMfaEnabled ? <ShieldCheck size={26} /> : <Shield size={26} />}
          </div>
          <div>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 700, margin: 0 }}>
              {isMfaEnabled ? "Manage Two-Factor Authentication" : "Set Up Two-Factor Authentication"}
            </h2>
            <p className="text-secondary" style={{ fontSize: "0.85rem", margin: 0 }}>
              Google Authenticator & Microsoft Authenticator
            </p>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: "1.25rem", fontSize: "0.85rem" }}>
            <AlertTriangle size={16} style={{ marginRight: "0.5rem", flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        {/* STEP 1: SETUP / QR CODE */}
        {step === "setup" && (
          <div>
            <p className="text-secondary" style={{ fontSize: "0.9rem", marginBottom: "1.25rem", lineHeight: 1.5 }}>
              Scan the QR code below using <strong>Microsoft Authenticator</strong>, <strong>Google Authenticator</strong>, or any standard TOTP authenticator app on your phone.
            </p>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
              {loading || !qrCodeUrl ? (
                <div style={{ width: "200px", height: "200px", display: "flex", alignItems: "center", justifyContent: "center", background: "#ffffff", borderRadius: "12px" }}>
                  <Loader2 className="animate-spin" size={32} style={{ color: "#333333" }} />
                </div>
              ) : (
                <div style={{ padding: "0.75rem", background: "#ffffff", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
                  <img 
                    src={qrCodeUrl} 
                    alt="MFA QR Code" 
                    style={{ width: "190px", height: "190px", display: "block" }} 
                  />
                </div>
              )}

              {/* Manual Entry Key */}
              <div style={{ width: "100%", background: "var(--bg-main)", border: "1px solid var(--border-color)", borderRadius: "8px", padding: "0.75rem 1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.25rem" }}>
                  <span className="text-secondary" style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase" }}>
                    Manual Entry Key
                  </span>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleCopyKey} 
                    style={{ padding: "0.2rem 0.6rem", fontSize: "0.75rem", height: "auto" }}
                  >
                    {copiedKey ? <Check size={12} style={{ color: "var(--success)", marginRight: "0.25rem" }} /> : <Copy size={12} style={{ marginRight: "0.25rem" }} />}
                    {copiedKey ? "Copied" : "Copy Key"}
                  </button>
                </div>
                <code style={{ fontSize: "0.9rem", letterSpacing: "1.5px", color: "var(--primary)", wordBreak: "break-all" }}>
                  {secret}
                </code>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() => setStep("verify")}
              >
                Next: Enter 6-Digit Code
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: VERIFY CODE */}
        {step === "verify" && (
          <form onSubmit={handleVerifyAndEnroll}>
            <p className="text-secondary" style={{ fontSize: "0.9rem", marginBottom: "1.5rem", lineHeight: 1.5 }}>
              Enter the 6-digit verification code currently shown in your authenticator app to complete enrollment.
            </p>

            <div className="form-group" style={{ marginBottom: "1.75rem", textAlign: "center" }}>
              <input
                type="text"
                autoFocus
                maxLength={6}
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                style={{
                  fontSize: "1.75rem",
                  letterSpacing: "8px",
                  textAlign: "center",
                  fontWeight: 700,
                  height: "56px",
                  width: "100%",
                  maxWidth: "240px",
                  margin: "0 auto",
                  borderRadius: "8px",
                  border: "2px solid var(--primary)",
                  backgroundColor: "var(--bg-main)",
                  color: "var(--text-primary)"
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem" }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setStep("setup")}
                disabled={loading}
              >
                Back to QR Code
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} style={{ marginRight: "0.5rem" }} />
                    Verifying...
                  </>
                ) : (
                  "Verify & Activate 2FA"
                )}
              </button>
            </div>
          </form>
        )}

        {/* STEP 3: BACKUP CODES */}
        {step === "backup_codes" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--success)", marginBottom: "0.75rem" }}>
              <CheckCircle2 size={20} />
              <span style={{ fontWeight: 600 }}>Two-Factor Authentication is now active!</span>
            </div>

            <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1rem", lineHeight: 1.4 }}>
              Save these emergency recovery codes. If you lose access to your phone or authenticator app, you can use these single-use codes to log into your account.
            </p>

            <div 
              style={{ 
                display: "grid", 
                gridTemplateColumns: "1fr 1fr", 
                gap: "0.5rem", 
                background: "var(--bg-main)", 
                border: "1px solid var(--border-color)", 
                borderRadius: "8px", 
                padding: "1rem",
                marginBottom: "1.25rem" 
              }}
            >
              {backupCodes.map((code, idx) => (
                <div key={idx} style={{ fontFamily: "monospace", fontSize: "0.95rem", fontWeight: 600, color: "var(--primary)", padding: "0.25rem 0.5rem" }}>
                  {code}
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleCopyBackupCodes}
                style={{ flex: 1 }}
              >
                {copiedBackupCodes ? <Check size={14} style={{ color: "var(--success)", marginRight: "0.25rem" }} /> : <Copy size={14} style={{ marginRight: "0.25rem" }} />}
                {copiedBackupCodes ? "Copied All" : "Copy Codes"}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleDownloadBackupCodes}
                style={{ flex: 1 }}
              >
                <Download size={14} style={{ marginRight: "0.25rem" }} />
                Download .txt
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={onClose}
              >
                I Have Saved My Backup Codes
              </button>
            </div>
          </div>
        )}

        {/* MANAGE ACTIVE MFA */}
        {step === "manage" && (
          <div>
            <div 
              style={{ 
                background: "rgba(76, 175, 80, 0.1)", 
                border: "1px solid var(--success)", 
                borderRadius: "8px", 
                padding: "1rem", 
                marginBottom: "1.5rem" 
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--success)", fontWeight: 600, marginBottom: "0.25rem" }}>
                <ShieldCheck size={18} />
                <span>Account Protected</span>
              </div>
              <p className="text-secondary" style={{ fontSize: "0.85rem", margin: 0 }}>
                Two-Factor Authentication is currently active on your account using Google or Microsoft Authenticator.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1.5rem" }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleRegenerateBackupCodes}
                disabled={loading}
                style={{ justifyContent: "flex-start", padding: "0.75rem 1rem" }}
              >
                <RefreshCw size={16} style={{ marginRight: "0.5rem" }} />
                Generate New Emergency Recovery Codes
              </button>

              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={() => setStep("confirm_disable")}
                disabled={loading}
                style={{ justifyContent: "flex-start", padding: "0.75rem 1rem" }}
              >
                <ShieldAlert size={16} style={{ marginRight: "0.5rem" }} />
                Disable Two-Factor Authentication
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* CONFIRM DISABLE */}
        {step === "confirm_disable" && (
          <div>
            <div 
              style={{ 
                background: "rgba(239, 68, 68, 0.1)", 
                border: "1px solid var(--danger)", 
                borderRadius: "8px", 
                padding: "1rem", 
                marginBottom: "1.5rem" 
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--danger)", fontWeight: 600, marginBottom: "0.25rem" }}>
                <AlertTriangle size={18} />
                <span>Disable Security Protection?</span>
              </div>
              <p className="text-secondary" style={{ fontSize: "0.85rem", margin: 0 }}>
                Disabling 2FA will lower your account security and remove the requirement for Microsoft/Google Authenticator codes during sign-in.
              </p>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setStep("manage")}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={handleDisableMFA}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} style={{ marginRight: "0.5rem" }} />
                    Disabling...
                  </>
                ) : (
                  "Confirm & Disable 2FA"
                )}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
